import Box from '@components/base/Box'
import TextLink from '@components/base/TextLink'
import { Event, EventTicket, TicketType } from '@graphql/__generated/graphql'
import EventDateTime from '@components/event/EventDateTime'
import DefaultThumbnail from '@public/images/default-thumbnail.png'
import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter } from 'react-icons/fa'

type EventRegistrationSummaryProps = {
  event?: Partial<Event>
  foundTicket?: EventTicket
  isInvoiceTicket: boolean
  invoiceTicketTotal: number
  currencySymbol?: string
  ticketTotalPrice?: number
  ticketQuantity?: number
  vatIncluded: boolean
}

const getSocialIcon = (platform: string) => {
  if (platform.toLowerCase() === 'facebook')
    return <FaFacebook className="h-8 w-8 my-1 mr-2 cursor-pointer" />
  if (platform.toLowerCase() === 'twitter')
    return <FaTwitter className="h-8 w-8 my-1 mr-2 cursor-pointer" />
  if (platform.toLowerCase() === 'instagram')
    return <FaInstagram className="h-8 w-8 my-1 mr-2 cursor-pointer" />
  if (platform.toLowerCase() === 'linkedin')
    return <FaLinkedin className="h-8 w-8 my-1 mr-2 cursor-pointer" />

  return <FaFacebook className="h-8 w-8 my-1 mr-2 cursor-pointer" />
}

const EventRegistrationSummary = ({
  event,
  foundTicket,
  isInvoiceTicket,
  invoiceTicketTotal,
  currencySymbol,
  ticketTotalPrice,
  ticketQuantity = 1,
  vatIncluded,
}: EventRegistrationSummaryProps) => {
  const mapsHref = event?.event?.eventLocationMapUrl
    ? event.event.eventLocationMapUrl
    : event?.event?.eventLocation
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.event.eventLocation)}`
      : undefined

  return (
    <Box className="w-full sm:w-1/2 px-6 flex flex-col">
      <Box className="text-3xl font-bold">{event?.event?.title}</Box>
      <Box className="flex items-center space-x-2 mt-2">
        <Image
          alt="Thumbnail Image"
          className="h-8 w-8 rounded-lg block object-cover"
          width={96}
          height={96}
          src={event?.event?.thumbnailImage ?? DefaultThumbnail}
        />
        <Box className="text-lg">{event?.event?.organizationName}</Box>
      </Box>
      <Box className="mt-4 line-clamp-8 text-sm">
        {event?.event?.shortDescription}
        {/* <RichTextDisplay */}
        {/*   descriptionJson={event?.event?.description || ''} */}
        {/* /> */}
      </Box>
                    <Box
                      show={
                        !!event?.event?.eventLocationMapLink &&
                        !!(event?.event?.eventLocationMapLink.length > 0)
                      }
                    >
                      <TextLink className="text-xs md:text-sm !text-gray-600">
                        {event?.event?.eventLocationMapLink}
                      </TextLink>
                    </Box>

                    <Box
                      show={
                        !!event?.event?.eventLocationMapUrl &&
                        !!(event?.event?.eventLocationMapUrl.length > 0)
                      }
                    >
                      <Link
                        href={event?.event?.eventLocationMapUrl ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs md:text-sm text-forumm-blue underline"
                      >
                        Open in Google Maps
                      </Link>
                    </Box>
      <Box className="flex my-4 space-x-6 mt-10">
        <Box className="flex items-center justify-start text-white mt-6 flex-wrap">
          {event?.communications?.socials?.map((social) => {
            return (
              <motion.div whileHover={{ scale: 1 }} key={social.platform}>
                <Link
                  href={social.url}
                  target={'_blank'}
                  data-testid="social-facebook"
                >
                  {getSocialIcon(social.platform)}
                </Link>
              </motion.div>
            )
          })}
        </Box>
      </Box>
      <Box className="flex my-4 space-x-6 mt-10">
        <Box className=" flex flex-col space-y-2">
          <Box className={`text-md font-medium`}>Start Date & Time</Box>
          <Box className="text-xs md:text-sm">
            <EventDateTime
              value={event?.event?.startDateTime}
              format="MMM DD, h:mmA"
              event={event?.event}
            />
          </Box>
        </Box>
        <Box className="flex flex-col space-y-2">
          <Box className={`text-md font-medium`}>End Date & Time</Box>
          <Box className="text-xs md:text-sm">
            <EventDateTime
              value={event?.event?.endDateTime}
              format="MMM DD, h:mmA"
              event={event?.event}
            />
          </Box>
        </Box>
      </Box>
      <Box className="flex text-right flex-col mt-10" show={foundTicket != null}>
        <Box className="text-xl">Ticket Cost</Box>
        <Box className="text-2xl">
          {isInvoiceTicket || foundTicket?.price === 0 ? (
            isInvoiceTicket ? (
              <>
                <Box className="text-xs mr-2 text-gray-400 inline">
                  Invoiced after registration.
                </Box>
                {`${currencySymbol}${invoiceTicketTotal.toFixed(2)}`}
              </>
            ) : (
              'FREE'
            )
          ) : (
            <>
              {foundTicket?.ticketType === TicketType.GroupGuest && (
                <Box className="mb-1 text-xs text-gray-400">
                  {ticketQuantity} tickets × {currencySymbol}
                  {foundTicket.price.toFixed(2)}
                </Box>
              )}
              <Box className="text-xs mr-2 text-gray-400 inline">
                Price
                {vatIncluded ? ' includes ' : ' does not include '}
                tax.
              </Box>
              {`${currencySymbol}${ticketTotalPrice?.toFixed(2)}`}{' '}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default EventRegistrationSummary
